import { z } from 'zod';
/**
 * Feature toggle keys shared between backend and frontend.
 *
 * ## How to add a new feature key
 *
 * 1. Add an entry to `FEATURE` below.
 * 2. Backend: apply `@RequireFeature(FEATURE.YOUR_KEY)` to the relevant controller.
 *    Currently gated controllers:
 *
 *    - ONBOARDING_TOUR → `apps/platform-api/src/modules/users/users.controller.ts`
 *      (`getOnboardingStatus`, `dismissOnboardingCard` handlers only); the only key seeded
 *      enabled by default (see `DEFAULT_ENABLED_FEATURES` in
 *      `apps/console-api/src/modules/feature-flags/feature-flags.constants.ts`) — every
 *      other key seeds disabled
 *    - IP_ALLOWLISTING →
 *      `apps/platform-api/src/modules/organization/organization.controller.ts` (`access`
 *      method only — the controller hosts other, ungated settings)
 *    - MILESTONE_SPLIT_INVOICING → `apps/platform-api/src/modules/quotes/quotes.controller.ts`
 *      (`convertSplit` method only — the controller hosts other, ungated quote actions)
 *    - TAX_COMPLIANCE_REPORTING →
 *      `apps/platform-api/src/modules/reports/reports.controller.ts` (`expenses`, `tax`,
 *      `basSummary`, `profitAndLoss` methods only); a manual `isEnabled()` check in
 *      `report-export/report-export.service.ts` for the same 4 report keys (the dispatch
 *      endpoint covers all 11 report keys, so a decorator can't scope to 4 of them);
 *      `report-pack/report-pack.controller.ts` (`eofy`, `receipts` methods)
 *    - TAGS → `apps/platform-api/src/modules/tags/tags.controller.ts` (class-level — every
 *      method is in scope); `reports.controller.ts` (`revenueByTag` method only)
 *    - ACCOUNTING_EXPORT →
 *      `apps/platform-api/src/modules/data-export/data-export.controller.ts`
 *      (`exportAccountingInvoices` method only — the other 4 CSV export methods stay
 *      ungated)
 *    - USER_MANAGEMENT → `apps/platform-api/src/modules/users/users.controller.ts` (the 9
 *      admin-only methods: list/invitations/summary/reset-password/invite/role/deactivate/
 *      reactivate — the `me/*` self-service methods stay ungated)
 *    - STRIPE_PAYMENTS → `apps/platform-api/src/modules/stripe/stripe.controller.ts`
 *      (class-level — every method is Stripe-specific); a manual `isEnabled()` check in
 *      `stripe-webhook.service.ts` that no-ops (200, not rejected) for a disabled tenant
 *      instead of a guard, so Stripe never sees a rejected delivery
 *    - USER_FEEDBACK →
 *      `apps/platform-api/src/modules/user-feedback/user-feedback.controller.ts`
 *      (class-level — every method is feedback-specific)
 * 3. Frontend: wrap the route in `<FeatureGate feature={FEATURE.YOUR_KEY}>` in the relevant
 *    TanStack Router route file. Wrap the sidebar nav item in `<FeatureGate>` in
 *    `AppSidebar.tsx`.
 * 4. Sys-admin: `FeatureFlagsService.seedForTenant()` seeds new keys during tenant
 *    provisioning. Existing tenants need a backfill — see
 *    `packages/db-tools/backfill-tenant-feature-toggles.ts`. Retiring a key follows the
 *    same pattern in reverse — see `packages/db-tools/retire-legacy-feature-flags.ts`.
 */
export const FEATURE = {
    ONBOARDING_TOUR: 'onboarding-tour',
    IP_ALLOWLISTING: 'ip-allowlisting',
    MILESTONE_SPLIT_INVOICING: 'milestone-split-invoicing',
    TAX_COMPLIANCE_REPORTING: 'tax-compliance-reporting',
    TAGS: 'tags',
    ACCOUNTING_EXPORT: 'accounting-export',
    USER_MANAGEMENT: 'user-management',
    STRIPE_PAYMENTS: 'stripe-payments',
    USER_FEEDBACK: 'user-feedback',
};
export const FeatureKeySchema = z.enum(Object.values(FEATURE));
export const UpdateFeatureToggleSchema = z.object({
    enabled: z.boolean(),
});
//# sourceMappingURL=feature-flags.js.map